<template>
  <div>
    <GameBoard ref="gameBoard"
               :size="$store.state.isMobile ? 0 : boardSize"
               :playMode="sealing ? 'button' : playMode"
               :state="state"
               :marks="marks"
               @action="onAction"
               />
    <!-- Choosing the sealed move takes a point and its button in either play mode (plan
         2026-10-29-sealed-moves.md). -->
    <div v-if="selected && (playMode === 'button' || sealing)" class="placement">
      <ActionButton v-if="sealing" :key="'seal:' + placement.key" :label="t('button-seal')"
                    :action="sealAction" :disabled="sealBusy" class="put" />
      <ActionButton v-else :key="placement.key" :label="t('button-place-stone')"
                    :action="placement.action" class="put" />
    </div>
    <div v-if="sealing" class="seal-panel">
      <p class="seal-line">{{ t('seal-hint') }}</p>
      <button type="button" class="seal-cancel" @click="cancelSeal">{{ t('button-seal-cancel') }}</button>
    </div>
    <div v-else-if="sealView && !messageBoxDef" class="seal-panel">
      <p class="seal-line">{{ sealView.line }}</p>
      <ActionButton v-if="sealView.action" :key="sealView.key" :label="sealView.label" :action="sealView.action"
                    :disabled="sealBusy" :variant="sealView.secondary ? 'secondary' : 'primary'"
                    :collect="!sealView.secondary" class="put" :class="{ secondary: sealView.secondary }" />
    </div>
    <component v-for="(section, i) in notificationSections" :is="section" :key="i"
               :state="ownNotifiable" :waiting="waitingForOpponent" />

    <div v-if="!selected && !messageBoxDef && !sealing" class="tools">
      <button class="estimate btn-secondary" @click="estimate">{{ t('button-estimate') }}</button>
      <!-- 聊天, right of 形势判断, which stays in the middle (SPEC §5.6). -->
      <ChatButton v-if="chat" class="chat-btn" :pressed="chat.shown" @click="$emit('chat-toggle')"/>
    </div>
    <!-- The AI hasn't answered 15 s into its turn (plan 2026-10-28-ai-games.md §4.2). -->
    <p v-if="aiSlow && !messageBoxDef" class="ai-slow">{{ t('ai-game-slow') }}</p>
    <PlayModeToggle v-if="!selected && !messageBoxDef && !sealing && !game.seal"/>
    <!-- KataGo's estimate, or waiting for one (plan 2026-10-29-katago-estimates.md); Pachi's counts. -->
    <template v-if="katagoLead !== null">
      <div class="estimate-katago">{{ t('estimate-katago', { lead: leadText(katagoLead) }) }}</div>
      <div class="estimate-hint">{{ t('estimate-katago-open') }}</div>
    </template>
    <div v-else-if="estimateWaiting" class="estimate-hint">{{ t('estimate-waiting') }}</div>
    <span>{{estimateText}}</span>
    <MessageBox v-model="messageBoxDef"/>
  </div>
</template>

<script>
import Game from '@/game/Game';
import { Stone } from '@/game/Board';
import GameBoard, { State, InfluenceChain, InputValidator, Action } from './GameBoard';
import { SimpleStoneChain } from './GameBoard/StoneChain';
import MessageBox from './MessageBox';
import PlayModeToggle from './PlayModeToggle';
import { t } from '@/texts';
import Session from '@/session/Session';
import ActionButton from './ActionButton';
import ChatButton from './ChatButton';
import extensions from '@/extensions';
import api from '@/api/api';
import { desktopBoardSize } from '@/utils';
import text, { countResultText } from '@/text';

// How long the AI may take before the page says it may be offline.
const AI_SLOW_MS = 15000;

class PlayInputValidator extends InputValidator {
  constructor(game, player) {
    super();
    this.playable = false;
    this.game = game;
    this.player = player;
  }

  get inputType() {
    if(this.playable && this.game &&
      this.game.status === 'in-progress' &&
      (this.player === this.game.nextPlayer || this.player === Stone.BOTH)) {
      return this.game.nextPlayer;
    } else {
      return null;
    }
  }

  validate(point) {
    return this.game && this.game.canPlay(point);
  }
}

export default {
  name: 'GameSession',

  props: {
    game: { type: Game, required: true },
    player: { type: Symbol },
    // The game's chat (chat.js), for the 聊天 button.
    chat: { type: Object, default: null },
  },

  data() {
    return {
      session: null,
      notifiable: {},
      notificationSections: extensions.notificationSections,
      selected: null,
      // The estimate asked for and not yet answered: { moveIndex }, or null.
      estimating: null,
      messageBoxDef: null,
      input: null,
      state: null,
      aiSlow: false,
      aiSlowTimer: null,
      // Choosing the move to seal (plan 2026-10-29-sealed-moves.md), a seal request under way, and
      // the pulse of a sealed stone just opened ({ key, x, y }).
      sealing: false,
      sealBusy: false,
      pulse: null,
      pulseTimer: null,
    }
  },

  watch: {
    'session.revision'() { this.selected = null; },
    'input.inputType'() { this.selected = null; },
    sealing(v) { this.$emit('sealing', v); },
    // The chat waits while a dialog (e.g. the count) is shown (SPEC §5.6).
    messageBoxDef(v) { this.$emit('dialog', !!v); },
    // The slow line counts from the moment the AI's turn began on this page.
    aiWaiting: {
      immediate: true,
      handler(waiting) {
        clearTimeout(this.aiSlowTimer);
        this.aiSlow = false;
        if(waiting) this.aiSlowTimer = setTimeout(() => { this.aiSlow = true; }, AI_SLOW_MS);
      },
    },
  },

  computed: {
    // On a desktop the board fits the window's height with the nav bar, the game line and the
    // buttons below it (SPEC §7.1).
    boardSize() {
      return desktopBoardSize(230);
    },

    turnKey() { return `${this.game.id}:${this.session.revision}:${this.game.moves.length}`; },
    placement() {
      const point = this.selected && { ...this.selected };
      const revision = this.session.revision, count = this.game.moves.length;
      return {
        key: `${this.turnKey}:${point && point.x}:${point && point.y}`,
        action: () => this.buttonPut(point, revision, count),
      };
    },
    opponent() {
      const u = this.$store.state.user;
      return u && [Stone.BLACK, Stone.WHITE].includes(this.player) && this.game.players.find(p => p.id !== u.id);
    },
    canNotify() { return !!this.opponent && !!(this.notifiable[this.opponent.id] || {}).possible; },
    waitingForOpponent() {
      return !!this.opponent && this.game.status === 'in-progress' && !this.session.isCounting && !this.game.seal &&
        this.game.nextPlayer !== this.player && this.game.moves.length === this.session.confirmedMoves;
    },
    // Waiting for the AI's move, in a game against it.
    aiWaiting() { return !!this.game.aiLevel && !!this.session && this.waitingForOpponent; },
    ownNotifiable() { const u = this.$store.state.user; return u ? this.notifiable[u.id] || {} : {}; },
    playMode() {
      return (this.$store.state.ui && this.$store.state.ui.playMode) || 'button';
    },

    // The seal button's action for the point chosen now.
    sealAction() {
      const point = this.selected && { ...this.selected };
      return () => this.sealMove(point);
    },

    // What shows under the board while a move is sealed: a line, and the player's button.
    sealView() {
      const seal = this.game.seal;
      if(!seal) return null;
      const me = this.$store.state.user;
      const mine = !!me && seal.sealedBy === me.id;
      const playing = !!me && [Stone.BLACK, Stone.WHITE].includes(this.player);
      const name = id => { const p = this.game.players.find(p => p.id === id); return p ? p.displayName : ''; };
      const other = playing ? this.game.players.find(p => p.id !== me.id) : null;
      const r = seal.request;
      if(!playing) return { line: t('sealed-other') };
      if(!r) return { key: 'ask', line: t(mine ? 'sealed-own' : 'sealed-other'), label: t('button-resume-request'), action: () => this.requestResume() };
      if(r.by === me.id) return { key: 'withdraw', line: t('resume-requested', { name: other ? other.displayName : '' }), label: t('button-resume-withdraw'), action: () => this.withdrawResume(), secondary: true };
      return { key: 'accept', line: t(mine ? 'resume-request-from' : 'resume-request-from-other', { name: name(r.by) }), label: t('button-resume-accept'), action: () => this.acceptResume() };
    },

    // The sealed stone for its sealer, and the pulse of a stone just opened.
    marks() {
      const list = [];
      if(this.game.seal && this.game.sealedPoint) list.push({ key: 'sealed', kind: 'sealed', text: '封', ...this.game.decode(this.game.sealedPoint) });
      if(this.pulse) list.push({ kind: 'pulse', ...this.pulse });
      return list;
    },

    // Black's lead in KataGo's estimate shown, or null (Pachi's, or none).
    katagoLead() {
      const inf = this.state.infChain;
      return inf && inf.katago !== undefined && inf.index === this.cursor ? inf.katago : null;
    },

    // Waiting for the estimate of the position shown; a move meanwhile ends it.
    estimateWaiting() {
      return this.estimating !== null && this.estimating.moveIndex === this.game.moves.length;
    },

    estimateText() {
      if(this.katagoLead !== null) return '';
      if(this.state.infChain && this.state.infChain.index === this.cursor)
        return `黑:${this.state.infChain.black}, 白:${this.state.infChain.white}`;
      else
        return "";
    },
  },

  components: {
    GameBoard,
    MessageBox,
    PlayModeToggle,
    ActionButton,
    ChatButton,
  },

  timers: {
    keepalive: { time: 3000, autostart: true, repeat: true }
  },

  beforeDestroy() {
    clearTimeout(this.aiSlowTimer);
    clearTimeout(this.pulseTimer);
    if (this.session) this.session.close();
  },

  methods: {
    t,
    // Asks first, like resigning (SPEC §5.3).
    async takeBackForOpponent() {
      const r = await this.messageBox(t('take-back-confirm'), t('button-confirm'), t('dialog-cancel'));
      if(r === 'ok') this.session.takeBackForOpponent();
    },

    resign() {
      this.session.resign();
    },

    // ---- sealed moves (plan 2026-10-29-sealed-moves.md)
    // 封手 in the game's actions: the next point chosen is the sealed move (one already chosen is).
    seal() {
      this.state.infChain = null;
      this.estimating = null;
      this.sealing = true;
    },

    cancelSeal() {
      this.sealing = false;
      if(this.$refs.gameBoard) this.$refs.gameBoard.clearSelection();
      this.selected = null;
    },

    sealUrl(rest = '') { return `${api.GAMES}/${this.game.id}/seals${rest}`; },

    sealError(e) {
      const code = (e && e.msg) || String(e);
      this.$emit('message', t('error-invalid-action', { reason: this.session.invalidMoveReason(code) || `:${code}` }), 'warn');
    },

    async sealMove(point) {
      if(!point || this.sealBusy || !this.sealing) return;
      if(!this.game.canPlay(point)) { this.$emit('message', this.game.unplayableReason, 'warn'); return; }
      this.sealBusy = true;
      try {
        await api.call('POST', this.sealUrl(), { index: this.game.moves.length, stone: Symbol.keyFor(this.game.nextPlayer), point: this.game.encode(point.x, point.y) });
        this.sealing = false;
        this.selected = null;
        await this.fetchSeal();
      } catch(e) {
        this.sealError(e);
        await this.fetchSeal();
      } finally {
        this.sealBusy = false;
      }
    },

    // The game's open seal, as the viewer may see it: the move only for its sealer.
    async fetchSeal() {
      try {
        const list = await api.call('GET', this.sealUrl());
        const open = (list || []).filter(s => !s.openedAt && !s.droppedAt).pop();
        this.game.setSeal(open ? { ...open, point: undefined } : null, (open && open.point) || null);
      } catch(e) {
        console.log(`seals: ${e.msg || e}`);
      }
    },

    async sealStep(rest) {
      if(this.sealBusy || !this.game.seal) return;
      this.sealBusy = true;
      try {
        await api.call('POST', this.sealUrl(`/${this.game.seal.n}${rest}`), {});
      } catch(e) {
        this.sealError(e);
      } finally {
        this.sealBusy = false;
        if(this.game.seal) await this.fetchSeal();
      }
    },

    requestResume() { return this.sealStep('/resume-requests'); },
    withdrawResume() { return this.game.seal.request && this.sealStep(`/resume-requests/${this.game.seal.request.id}/withdrawal`); },
    acceptResume() { return this.game.seal.request && this.sealStep(`/resume-requests/${this.game.seal.request.id}/acceptance`); },

    count() {
      this.session.startCount();
    },

    keepalive() {
      this.session.keepalive();
    },

    buttonPut(point, revision, count) {
      if (!point || !this.selected || point.x !== this.selected.x || point.y !== this.selected.y ||
          this.session.disposed || this.session.revision !== revision || this.game.moves.length !== count ||
          this.session.confirmedMoves !== count || this.input.inputType === null) return;
      this.put(point);
      this.selected = null;
    },

    put(point) {
      console.log(`put(${point})`);
      this.session.expectNotice = this.canNotify;
      const ok = this.game.play(point);
      if(!ok) { this.session.expectNotice = false; this.$emit('message', this.game.unplayableReason); }
    },

    messageBox(txt, okText=null, cancelText=null, detail=null) {
      return new Promise(resolve => {
        this.messageBoxDef = {
          text: txt,
          detail,
          okText,
          cancelText,
          ok: okText ? () => { resolve('ok'); } : null,
          cancel: cancelText ? () => { resolve('cancel'); } : null,
        }
      });
    },

    // Shows the estimate of the position (SPEC §5.1, §11.1): KataGo's when the site could reach it,
    // else Pachi's; a second tap hides it, or stops waiting for it.
    async estimate() {
      if(this.state.infChain !== null || this.estimating !== null) {
        this.state.infChain = null;
        this.estimating = null;
        return;
      }
      const asked = this.estimating = { moveIndex: this.game.moves.length };
      let r;
      try {
        r = await api.post(api.ESTIMATES, { game: this.game.id, moveIndex: asked.moveIndex });
      } finally {
        if(this.estimating !== asked) r = null;
        else this.estimating = null;
      }

      if(r && r.data.moveIndex === this.game.moves.length) {
        const chain = new InfluenceChain(this.game, this.cursor, r.data.countResult.pointMap);
        if(r.data.engine === 'katago') chain.katago = r.data.katago.scoreLead;
        this.state.infChain = chain;
      }
    },

    leadText(lead) {
      return Math.abs(lead) < 0.5 ? t('chart-even')
        : t('chart-lead', { color: t(lead < 0 ? 'color-white' : 'color-black'), points: Math.abs(lead).toFixed(1) });
    },

    onAction(action) {
      switch(action.type) {
        case Action.Type.PUT:
          this.state.infChain = null;
          this.estimating = null;
          if(action.point) this.put(action.point);
          break;

        case Action.Type.SELECT:
          this.state.infChain = null;
          this.estimating = null;
          this.selected = action.point;
          break;
      }
    },
  },

  created() {
    this.input = new PlayInputValidator(this.game, this.player);
    this.state = new State(this.game.grid, new SimpleStoneChain(this.game.moves), null,  null, this.input);

    const self = this;
    this.game.addWatcher({
      // eslint-disable-next-line no-unused-vars
      onMove(move) {
        self.state.stoneChain.index = self.game.moves.length;
        self.$audio.play('stone');
        // A sealed move just opened pulses, so that nobody misses where it went.
        if(move.sealedAt && !move.move.startsWith('.')) {
          clearTimeout(self.pulseTimer);
          self.pulse = { key: `pulse-${move.index}`, x: move.x, y: move.y };
          self.pulseTimer = setTimeout(() => { self.pulse = null; }, 3200);
        }
      },

      // eslint-disable-next-line no-unused-vars
      onTakeBack(move) {
        self.state.stoneChain.index = self.game.moves.length;
      },
    });

    if(this.game.seal) this.fetchSeal();
    this.session = new Session(this.game, this.player, {
      afterDraw: () => self.$nextTick().then(() => new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))),
      onNotifiable(msg) { self.$set(self.notifiable, msg.user.id, msg); },
      onPresence(msg) { self.$emit('presence', msg); },
      onChat(m) { self.$emit('chat', m); },
      onJoined() { self.$emit('joined'); },
      onNoticeProblem(msg) {
        const tags = { 'not-enabled': 'notify-opponent-not-sent', failed: 'notify-opponent-failed', unconfirmed: 'notify-opponent-unconfirmed' };
        if (self.canNotify && tags[msg.status]) self.$emit('message', t(tags[msg.status]), 'warn');
      },
      onError(err) {
        self.$emit('message', err, 'warn');
      },

      onPlay() {
        self.game.setSeal(null, null);
        self.input.playable = true;
        self.messageBoxDef = null;
        self.state.infChain = null;
      },

      // A move is sealed, or a request to resume it changed: read the seal again.
      onSealed() {
        self.input.playable = false;
        self.sealing = false;
        self.selected = null;
        self.fetchSeal();
      },

      onCompleted() {
        self.input.playable = false;
        self.$emit('completed');
      },

      async onCountRequest(stone) {
        self.input.playable = false;
        // In a game against the AI, the other colour's request is the AI's (its pass).
        const asked = self.game.aiLevel && stone !== self.player ? t('ai-count-request') : `${text.stoneLong(stone)}请求数子`;
        const r = await self.messageBox(asked, '同意', '不同意');
        return r === 'ok';
      },

      async onCountRequestSent(stone) {
        self.input.playable = false;
        await self.messageBox(`已请求数子, 等待${text.stoneLong(Game.invert(stone))}同意`, '取消');
      },

      async onCountResult(result) {
        self.input.playable = false;
        self.state.infChain = new InfluenceChain(self.game, self.cursor, result.pointMap);
        // The result being confirmed, in 子, with the winner by the rule of SPEC §5.5.
        const score = countResultText(self.game, result);
        const r = await self.messageBox(t('count-result-confirm'), t('button-confirm'), t('dialog-cancel'), score);
        self.state.infChain = null;
        return r === 'ok';
      },

      onCountConfirmSent(stone) {
        self.input.playable = false;
        self.messageBox(`已确认数子结果, 等待${text.stoneLong(Game.invert(stone))}确认`);
      },

      async confirm(txt) {
        const r = await self.messageBox(txt, '确认', '取消');
        return r === 'ok';
      },
    });
  },
}
</script>

<style scoped>
/* The place-stone button is the page's main button (wood brown); inside WeChat the native button
   copies its colour, radius and size (SPEC §5.1). */
.placement .put, .seal-panel .put {
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 300px;
  max-width: 100%;
  height: 46px;
  margin-left: auto;
  margin-right: auto;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: #8b5e34;
  color: #fff;
  font-size: 17px;
  line-height: normal;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .25);
  cursor: pointer;
}

.estimate {
  margin-top: 10px;
  width: 200px;
}

.placement { margin: 10px auto; max-width: 300px; }

.estimate-katago {
  margin-top: 8px;
  color: #2c3e50;
  font-size: 15px;
}

.estimate-hint {
  margin: 2px auto 0;
  color: #888;
  font-size: 13px;
}

.tools { position: relative; }

.seal-panel {
  margin: 10px 16px 4px;
  text-align: center;
}

.seal-line {
  margin: 0 auto 10px;
  max-width: 420px;
  color: #5a3e1f;
  font-size: 15px;
  line-height: 1.4;
}

.seal-cancel {
  border: none;
  background: none;
  color: #0175ff;
  font-size: 15px;
  cursor: pointer;
}

.seal-panel .put.secondary {
  width: 200px;
  height: 42px;
  border: 1px solid #c8b28a;
  background: #fff;
  color: #2c3e50;
  font-size: 16px;
  box-shadow: none;
}

.ai-slow {
  color: #888;
  font-size: 15px;
  margin: 8px auto;
  padding: 0 16px;
  max-width: 420px;
}
.tools .chat-btn {
  position: absolute;
  left: calc(50% + 112px);
  top: 7px;
}
</style>
